import { useEffect } from "react";
import { geolocation } from "@/shared/geo/geolocationStore";
import * as Location from "@/shared/platform/location";
import { updateAgentLocation } from "../services/agentService";
import { useSessionStore } from "../../../shared/stores/sessionStore";
import { useAgentStore } from "../stores/agentStore";

// Pousse la position GPS de l'agent vers POST /agents/{id}/location tant qu'il est
// AVAILABLE ou BUSY (stoppé OFFLINE). Trois mécanismes complémentaires :
//   • push IMMÉDIAT au passage online → l'agent est tout de suite assignable ;
//   • watchPositionAsync → push à chaque déplacement (>25 m) ;
//   • HEARTBEAT → re-push la dernière position MÊME à l'arrêt, pour que
//     `last_location_at` reste frais. ⚠️ Un veilleur repasse toute Main OFFLINE
//     après QUINZE minutes de silence ; sans heartbeat, une Main immobile
//     s'éteint sans le savoir (watchPositionAsync ne déclenche pas sans
//     mouvement) et ne reçoit plus aucune offre.
// Erreurs réseau silencieuses (tracking best-effort).

// 5 s / 10 m : suivi dense pour un déplacement fluide sur la carte client
// (la moto glisse entre les pings — plus ils sont rapprochés, plus c'est continu).
const WATCH_INTERVAL_MS = 5_000;
const WATCH_DISTANCE_M  = 10;
/**
 * Cadence du battement de cœur — §5 du contrat du 2026-09-02.
 *
 * Le contrat est explicite : « La position doit repartir toutes les 30 à
 * 60 secondes. » On prend 45 s, au milieu de la fourchette.
 *
 * ## Pourquoi les 2 minutes précédentes ne suffisaient plus
 * Le commentaire d'origine visait « la limite 30 min du backend ». Cette limite
 * est passée à QUINZE minutes, et le reste de l'app le sait déjà
 * (`agentEligibility`, `useAgentPresence`, `EligibilityBanner`). Deux minutes
 * restaient sous le seuil, mais laissaient sept battements manquer avant
 * l'extinction — sur un réseau mobile béninois, c'est un scénario ordinaire,
 * pas un cas limite.
 *
 * L'enjeu n'est pas théorique : au 2 septembre, « les sept mains de la
 * plateforme sont hors ligne et quatre n'ont jamais envoyé une seule
 * position ». Une Main éteinte par le veilleur ne reçoit plus AUCUNE offre, et
 * rien à son écran ne le lui dit — c'est `EligibilityBanner` qui l'en avertit,
 * mais mieux vaut ne pas s'y rendre.
 */
const HEARTBEAT_MS      = 45_000;

type Coords = { latitude: number; longitude: number; accuracyMeters?: number };

export function useAgentLocationTracker() {
  const agentId = useSessionStore((s) => s.agentId);
  const status  = useAgentStore((s) => s.status);

  useEffect(() => {
    if (!agentId)                                    return;
    if (status !== "AVAILABLE" && status !== "BUSY") return;

    let cancelled = false;
    let subscription: Location.LocationSubscription | null = null;
    let heartbeat: ReturnType<typeof setInterval> | null = null;
    let lastCoords: Coords | null = null;

    const push = (coords: Coords) => {
      lastCoords = coords;
      // Le store partagé profite de CHAQUE position émise : les autres écrans
      // agent (zone, carte de mission) la réutilisent au lieu de rallumer le
      // GPS pour leur compte.
      geolocation.reportPosition({ latitude: coords.latitude, longitude: coords.longitude });
      updateAgentLocation(agentId, coords).catch(() => { /* best-effort */ });
    };

    (async () => {
      // Se mettre EN LIGNE est une action délibérée qui exige une position :
      // sans elle, le backend n'auto-assigne pas l'agent et la carte du client
      // reste sur « En attente de la position de la Main ».
      //
      // `requirePosition` — et non `ensurePosition` : l'agent doit SAVOIR que sa
      // localisation est coupée. Auparavant l'échec était silencieux, et il se
      // croyait disponible en ne recevant aucune course, sans rien à l'écran qui
      // l'explique.
      const coord = await geolocation.requirePosition(0);
      if (!coord || cancelled) return;

      // Push immédiat — l'agent devient assignable dès qu'il se met en ligne.
      // Accuracy HIGH : force le provider GPS (suivi de déplacement précis, à la
      // façon des apps VTC). Balanced (fused provider) délivrait des positions
      // périmées — et ignorait le GPS simulé des émulateurs en dev.
      try {
        const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High });
        if (!cancelled) {
          push({
            latitude:       pos.coords.latitude,
            longitude:      pos.coords.longitude,
            accuracyMeters: pos.coords.accuracy ?? undefined,
          });
        }
      } catch { /* GPS off / timeout — le watch ci-dessous prendra le relais */ }

      // Push sur déplacement.
      try {
        subscription = await Location.watchPositionAsync(
          {
            accuracy:         Location.Accuracy.High,
            timeInterval:     WATCH_INTERVAL_MS,
            distanceInterval: WATCH_DISTANCE_M,
          },
          (loc) => {
            if (cancelled) return;
            push({
              latitude:       loc.coords.latitude,
              longitude:      loc.coords.longitude,
              accuracyMeters: loc.coords.accuracy ?? undefined,
            });
          },
        );
      } catch { /* permissions GPS, GPS off, etc. — silencieux */ }

      // Heartbeat : maintient `last_location_at` frais même immobile.
      heartbeat = setInterval(() => {
        if (cancelled || !lastCoords) return;
        push(lastCoords);
      }, HEARTBEAT_MS);
    })();

    // ── Le dernier point AVANT le gel, et le premier APRÈS ──────────────────
    //
    // Quand la Main bascule sur une autre application ou verrouille son
    // téléphone, la page est gelée : plus de `watchPosition`, plus de
    // battement. Le client reste alors sur la dernière position reçue, qui peut
    // dater de la minute précédente — le temps que le battement soit passé.
    //
    // Deux gestes gratuits réduisent cet écart aux deux extrémités :
    //
    //   • à la MISE EN ARRIÈRE-PLAN, on renvoie tout de suite la dernière
    //     position connue. `visibilitychange` est le dernier moment garanti où
    //     du code peut encore s'exécuter — la position gelée est alors la plus
    //     fraîche possible, au lieu d'être vieille de presque un battement ;
    //
    //   • au RETOUR au premier plan, on renvoie sans attendre le battement
    //     suivant. La carte du client rattrape la vérité à la seconde où la
    //     Main regarde son téléphone, et non quarante-cinq secondes plus tard.
    //
    // ⚠️ Cela ne remplace rien : pendant le gel, aucune position n'est produite,
    // et aucune API web ne permettrait d'en produire. Cela borne seulement
    // l'erreur de part et d'autre.
    const onVisibility = () => {
      if (cancelled || !lastCoords) return;
      push(lastCoords);
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      subscription?.remove();
      if (heartbeat) clearInterval(heartbeat);
    };
  }, [agentId, status]);
}
